import { useMemo, useState } from 'react'
import { gql, useQuery, useLazyQuery } from '@apollo/client'
import moment from 'moment'
import { useSites } from 'store'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'

const USERS_OPERATION = gql`
  query PortalUsersSearch($createdAtStart: String, $createdAtEnd: String, $siteSfIds: [ID!], $countries: [String!], $offset: Int, $limit: Int, $verbatim: String, $sortBy: String, $sortDirection: String, $includeAccount: Boolean!) {
    users: portalUsersSearch(created_at_start: $createdAtStart, created_at_end: $createdAtEnd, site_sf_ids: $siteSfIds, countries: $countries, verbatim: $verbatim, offset: $offset, limit: $limit, sort_by: $sortBy, sort_direction: $sortDirection) {
      id
      email
      country
      civility
      site {
        id 
        name
      }
      account_parent @include(if: $includeAccount) {
        sf_id
        name
      }
      first_name
      last_name
      created_at
      browser_name
      os_name
    }
  }
`

const EXPORT_USERS_OPERATION = gql`
  query PortalUsersExport($createdAtStart: String, $createdAtEnd: String, $siteSfIds: [ID!], $countries: [String!], $verbatim: String, $language: String, $fields: [String!], $sortBy: String, $sortDirection: String) {
    portalUsersExport(created_at_start: $createdAtStart, created_at_end: $createdAtEnd, site_sf_ids: $siteSfIds, countries: $countries, verbatim: $verbatim, language: $language, fields: $fields, sort_by: $sortBy, sort_direction: $sortDirection)
  }
`

const LIMIT = 25

const serializeDate = ({start, end}) => {
  const createdAtStart = start ? moment(start).format() : null
  const createdAtEnd = end ? moment(end).endOf('day').format() : null
  return {createdAtStart, createdAtEnd}
}

const useQueryVariables = ({
  verbatim,
  countries,
  startDate,
  endDate,
  sortBy,
  sortDirection,
  limit = LIMIT
}) => {
  const [{ selected: sitesSelected }] = useSites()

  const serializedDate = useMemo(() => {
    return serializeDate({start: startDate, end: endDate})
  }, [startDate, endDate])
  const siteSfIds = useMemo(
    () => (sitesSelected ? sitesSelected.map(({ sf_id }) => sf_id) : []),
    [sitesSelected],
  )
  return useMemo(() => {
    return {
      siteSfIds: siteSfIds,
      offset: 0,
      limit,
      verbatim,
      countries,
      sortBy: sortBy,
      sortDirection: sortDirection,
      ...serializedDate
    }
  }, [siteSfIds, verbatim, countries, serializedDate, limit, sortBy, sortDirection])
}

export const useExportCustomers = ({
  verbatim,
  countries,
  startDate,
  endDate,
  language,
  sortBy,
  sortDirection
}) => {
  const {t} = useTranslation()
  const queryVariables = useQueryVariables({
    verbatim,
    countries,
    startDate,
    endDate,
    sortBy,
    sortDirection
  })

  const [launchExport, { error, loading }] = useLazyQuery(EXPORT_USERS_OPERATION)
  const handleLaunchExport = async (fields) => {
    await launchExport({
      variables: {
        ...queryVariables,
        fields,
        language
      },
      fetchPolicy: 'network-only'
    })
    
    toast.success(t('customers.export.success'))
  }

  return {
    loading,
    error,
    launchExport: handleLaunchExport
  }
}

const useUsers = ({
  verbatim,
  countries,
  startDate,
  endDate,
  sortBy,
  sortDirection
}) => {
  const [shouldFetchNextPage, setShouldFetchNextPage] = useState(true)
  const queryVariables = useQueryVariables({
    verbatim,
    countries,
    startDate,
    endDate,
    sortBy,
    sortDirection
  })
  
  const { error, loading, data, fetchMore, networkStatus } =
    useQuery(USERS_OPERATION, {
      notifyOnNetworkStatusChange: true,
      variables: {
        ...queryVariables,
        includeAccount: true
      }
    })
  
  const users = useMemo(() => (data ? data.users : []), [data])
  
  const hasCivility = useMemo(() => {
    return users.some(user => user.civility)
  }, [users])

  const fetchNextPage = async () => {
    try {
      const fetchMoreResult = await fetchMore({
        variables: {
          offset: users.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          
          return {
            ...prev,
            users: [...prev.users, ...fetchMoreResult.users]
          }
        }
      });
      
      if (
        !fetchMoreResult.data.users.length ||
        fetchMoreResult.data.users.length < LIMIT
      ) {
        setShouldFetchNextPage(false);
      }
    } catch (error) {
      console.error('Error fetching next page:', error);
      setShouldFetchNextPage(false);
    }
  };

  return {
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage:
      !loading && users && users.length >= LIMIT && shouldFetchNextPage,
    error,
    users,
    hasCivility,
    fetchNextPage,
  }
}

export default useUsers